import { useIntl } from "react-intl";
import { Link } from "react-router";

import { cn } from "~/lib/ui";

import messages from "./messages";

/**
 * SFCC-11498 (ARTICLES-12) — shared presentational article tile. Rendered both
 * by `ArticleCard` (custom mode, from a resolved reference) and by the dynamic
 * `ArticleGrid` (from fetched tile DTOs) so the two paths are pixel-identical.
 *
 * Markup + Tailwind mirror composable's `articleCard.jsx` (production parity):
 * image 40% height (`h-2/5`), centered `line-clamp-6` title, underlined CTA
 * pinned to the bottom; card heights `h-[18.75rem]` / `sm:h-[25rem]`.
 */

export interface ArticleTileProps {
  image?: string;
  title?: string;
  url: string;
  ctaText?: string;
  /** noWrap attributes injected by Builder; spread on the root element. */
  attributes?: Record<string, unknown>;
}

export function ArticleTile({
  image,
  title,
  url,
  ctaText,
  attributes,
}: ArticleTileProps) {
  const intl = useIntl();
  // SFCC-11723 — localized default; an explicit `ctaText` (e.g. a Builder
  // per-entry override) still wins.
  const cta = ctaText ?? intl.formatMessage(messages.readMore);
  const { className, ...rest } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  return (
    <Link
      to={url}
      className={cn(
        "mx-auto flex h-[18.75rem] w-full flex-col overflow-hidden rounded-lg border border-solid border-stone-200 sm:h-[25rem]",
        className,
      )}
      {...rest}
      data-testid="articleCard"
    >
      {image ? (
        <img
          src={image}
          alt={title ?? ""}
          loading="lazy"
          className="h-2/5 w-full rounded-t-lg object-cover"
        />
      ) : (
        <div className="h-2/5 w-full rounded-t-lg bg-stone-100" />
      )}

      <div className="px-1 py-4 text-center lg:px-3">
        <h3
          className="line-clamp-6 text-sm leading-5 lg:text-base lg:leading-7"
          data-testid="articleCard-Heading"
        >
          {title}
        </h3>
      </div>

      <div className="mt-auto p-2 text-center">
        <span
          className="inline text-sm font-medium tracking-[.07rem] text-emphasis underline hover:text-emphasis/90"
          data-testid="articleCard-Cta"
        >
          {cta}
        </span>
      </div>
    </Link>
  );
}

export default ArticleTile;
